import React from 'react';
import { useParams } from 'react-router-dom';

import { Container, SimpleGrid, Space } from '@mantine/core';
import { Download } from 'lucide-react';

import { SummaryCard } from './SummaryCard';
import { SummaryStatisticCard } from './SummaryStatisticCard';
import { exportSummaryToCSV } from './exportSummaryUtils';
import type { CardData } from './interfaces';
import { numberWithCommas } from './interfaces';
import { LoadingContainer } from '../../common/LoadingContainer';
import { DATE_FORMAT_OPTIONS } from '../../constants/date-constants';
import { useEnsureSelectedClonotypeDefinition } from '../../hooks/useEnsureSelectedClonotypeDefinition';
import { useGetAntibodySummaryData, useGetExperiment, useGetSummaryData } from '../../store/store';
import { formatDate, getClonotypeEnumFromDefinition } from '../../utils';

const s = {
  // todo: finish updating this component with styles all defined here instead of inline, and remove old inline styles
  exportIcon: 'w-4 h-4',
  floatingBar: 'sticky bottom-0 z-30 flex items-center justify-end pr-2 py-3 bg-white border-t border-[#e1e5e9]',
  exportBtn:
    'px-6 py-3 text-sm font-medium text-white bg-[#0064b0] rounded-[44px] hover:bg-[#0053a0] inline-flex items-center gap-2.5 transition-colors border-0 cursor-pointer',
} as const;

export function Summary() {
  const { id } = useParams();
  const { data: experiment, isLoading: isLoadingExperiment } = useGetExperiment(
    {
      id: id!,
    },
    {
      skip: !id,
    },
  );

  const { data: summary, isLoading: isLoadingSummaryData } = useGetSummaryData(
    {
      id: id!,
    },
    {
      skip: !id,
    },
  );

  const selectedClonotypeDefinition = useEnsureSelectedClonotypeDefinition();

  const { data: antibodySummary, isLoading: isAntibodySummaryLoading } = useGetAntibodySummaryData(
    {
      id: id!,
      clonotypeDefinition: getClonotypeEnumFromDefinition(selectedClonotypeDefinition),
    },
    {
      skip: !id || !selectedClonotypeDefinition,
    },
  );

  if (!id) {
    return null;
  }

  // TODO: Better do this per component instead of globally
  if (isLoadingSummaryData || isLoadingExperiment || !experiment) {
    return <LoadingContainer height={600} text="Fetching data..." />;
  }

  // Row names and data for Immunoglobulin expression summary
  const immunoglobulinExpressionData: CardData[] = [
    {
      label: 'Productive VH/VL',
      value: summary?.cellsWithProdVhVl,
      info: 'Total unique cells with a paired, in-frame, and stop-codon-free heavy/light chain pair.',
    },
    {
      label: 'Unproductive VH/VL',
      value: summary?.cellsWithUnprodVhVl,
      info: 'Total unique cells with paired or unpaired heavy/light chains that are out-of-frame or contain stop codons.',
    },
    {
      label: 'VL only cells',
      value: summary?.singletonVl,
      info: 'Total unique cells containing only a productive light chain sequence with no paired heavy chain.',
    },
    {
      label: 'VH only cells',
      value: summary?.singletonVh,
      info: 'Total unique cells containing only a productive heavy chain sequence with no paired light chain.',
    },
    {
      label: 'Median IGH UMIs per cell',
      value: summary?.medianIghUmiPerCell,
      info: 'The median count of unique molecular identifiers detected per called B cell barcode (includes only productive chains).',
    },
    {
      label: 'Median IGK UMIs per cell',
      value: summary?.medianIgkUmiPerCell,
      info: 'The median count of unique molecular identifiers detected per called B cell barcode (includes only productive chains).',
    },
    {
      label: 'Median IGL UMIs per cell',
      value: summary?.medianIglUmiPerCell,
      info: 'The median count of unique molecular identifiers detected per called B cell barcode (includes only productive chains).',
    },
  ];

  // Row names and data for antibody clonotypes summary
  const antibodyClonotypesData: CardData[] = [
    {
      label: 'Total clonotypes',
      value: isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.totalClonotypes ?? '--'),
    },
    {
      label: 'Clonotypes > 1 member',
      value: isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.multipleCellClonotypes ?? '--'),
    },
    {
      label: 'Clonotypes with 1 member',
      value: isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.singleCellClonotypes ?? '--'),
    },
  ];

  // Row names and data for V gene summary
  const vGeneAndIsotypesCapturedData = [
    {
      label: 'Number of V genes: heavy',
      value: summary?.vhGenesUsed,
    },
    {
      label: 'Number of V genes: kappa',
      value: summary?.vkGenesUsed,
    },
    {
      label: 'Number of V genes: lambda',
      value: summary?.vlGenesUsed,
    },
  ];

  // Row names and data (dynamically generated) for isotypes summary
  const isotypesCapturedData: CardData[] = Array.isArray(summary?.igIsotypes)
    ? summary.igIsotypes.map((isotypeEntry: string) => {
        const [rawLabel = '', rawValue] = isotypeEntry.split(':');
        const label = rawLabel.trim();
        const value = rawValue?.trim();

        return {
          label,
          value: value !== undefined && value !== '--' ? `${value}%` : '--',
        };
      })
    : [];

  function getVGeneSummaryBlock() {
    return vGeneAndIsotypesCapturedData.slice(0, -1).map((item: CardData) => {
      const formattedValue = typeof item.value === 'number' ? numberWithCommas(item.value) : item.value;
      return <SummaryCard key={item.label} cardText={item.label} cardValue={formattedValue} />;
    });
  }

  const lastVGeneEntry = vGeneAndIsotypesCapturedData.at(-1);
  const rawLastVGeneValue = lastVGeneEntry?.value;
  const lastVGeneValue = Array.isArray(rawLastVGeneValue)
    ? rawLastVGeneValue.map((item: string) => item.replace('GH', 'g')).join(', ')
    : typeof rawLastVGeneValue === 'number'
      ? numberWithCommas(rawLastVGeneValue)
      : rawLastVGeneValue;

  const handleExportCSV = () => {
    const allVGenesData = [...vGeneAndIsotypesCapturedData];
    const fileSafeExperimentTitle = (experiment?.title || 'export').replace(/[^a-zA-Z0-9-_]/g, '_');
    const isoDateForFilename = experiment?.creationDate ? new Date(experiment.creationDate).toISOString().split('T')[0] : 'unknown-date';

    exportSummaryToCSV(
      {
        experimentInfo: {
          name: experiment?.title ?? '--',
          date: experiment?.creationDate ? formatDate(experiment.creationDate, DATE_FORMAT_OPTIONS.FULL_DATE) : '--',
        },
        topMetrics: {
          totalCells: summary?.cellsBeforeFiltering ? numberWithCommas(summary?.cellsBeforeFiltering) : '--',
          cellsWithPairedVHVL: summary?.cellsWithProdVhVl ? numberWithCommas(summary?.cellsWithProdVhVl) : '--',
          antibodyClonotypes: isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.totalClonotypes ?? '--'),
        },
        vGenes: allVGenesData,
        immunoglobulinExpression: immunoglobulinExpressionData,
        antibodyClonotypes: antibodyClonotypesData,
        isotypes: isotypesCapturedData,
      },
      `${isoDateForFilename}_summary_${fileSafeExperimentTitle}.csv`,
    );
  };

  return (
    <Container mx={0} fluid mb="xl" py="md" px="xs">
      <Space mt="md" />
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        {experiment.antibodyFile && (
          <SummaryCard
            cardText="Total number of cells"
            cardValue={summary?.cellsBeforeFiltering ? numberWithCommas(summary?.cellsBeforeFiltering) : null}
            cardInfo="Count of unique barcodes in the file that have at least one high-confidence, productive contig."
          />
        )}

        <SummaryCard
          cardText="Productive VH/VL"
          cardValue={summary?.cellsWithProdVhVl ? numberWithCommas(summary?.cellsWithProdVhVl) : null}
          cardInfo="Total unique cells with a paired, in-frame, and stop-codon-free heavy/light chain pair.
"
        />
        <SummaryCard cardText="Total clones" cardValue={isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.totalNodes ?? '--')} />
        <SummaryCard cardText="Total clonotypes" cardValue={isAntibodySummaryLoading ? 'Fetching...' : (antibodySummary?.totalClonotypes ?? '--')} />
      </SimpleGrid>
      <Space mt="xl" />
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {getVGeneSummaryBlock()}
        <SummaryCard cardText={lastVGeneEntry?.label || ''} cardValue={lastVGeneValue} />
      </SimpleGrid>
      <Space mt="xl" />

      <SimpleGrid cols={2}>
        <SummaryStatisticCard cardTitle="Immunoglobulin" cardData={immunoglobulinExpressionData} />
        <SummaryStatisticCard cardTitle="Immunoglobulin isotypes" cardData={isotypesCapturedData} />
      </SimpleGrid>

      <div className={s.floatingBar}>
        <button type="button" className={s.exportBtn} onClick={handleExportCSV}>
          <Download className={s.exportIcon} />
          Export as CSV
        </button>
      </div>
    </Container>
  );
}
